"use client";

import { useState, useCallback } from "react";
import { ParseResult } from "papaparse";
import { parseCSVClient } from "@/lib/csv";

export type CSVUploadState<T = Record<string, unknown>> = {
  results: ParseResult<T> | undefined;
  isLoading: boolean;
  error: string | null;
};

export type CSVUploadOptions = {
  onComplete?: (results: ParseResult<any>, files: File[]) => void;
  onError?: (error: Error, file?: File) => void;
  useWorker?: boolean;
};

export function useCSVUpload<T = Record<string, unknown>>(options: CSVUploadOptions = {}) {
  const [state, setState] = useState<CSVUploadState<T>>({
    results: undefined,
    isLoading: false,
    error: null,
  });

  const uploadFile = useCallback(
    async (file: File) => {
      if (!file) return;

      setState(prev => ({ ...prev, isLoading: true, error: null }));

      parseCSVClient<T>(file, {
        onComplete: results => {
          if (!results.errors.length) {
            setState(prev => ({
              ...prev,
              results,
              isLoading: false,
            }));
          } else {
            setState(prev => ({
              ...prev,
              results,
              error: results.errors[0].message,
              isLoading: false,
            }));
          }
          options.onComplete?.(results, [file]);
        },
        onError: (error, file) => {
          const errorMessage = error instanceof Error ? error.message : String(error);
          setState(prev => ({ ...prev, error: errorMessage, isLoading: false }));
          options.onError?.(error, file);
        },
        config: {
          worker: options.useWorker,
        },
      });
    },
    [options]
  );

  const reset = useCallback(() => {
    setState({
      results: undefined,
      isLoading: false,
      error: null,
    });
  }, []);

  return {
    ...state,
    uploadFile,
    reset,
  };
}
